/*
 * Bewusst /max und nicht /min: die kleine Metadaten-Variante prüft nur grob die Länge und
 * kennt keine Nummerntypen. Sie hält "1222", "12345" und "0170 1" für gültige deutsche
 * Nummern. /max unterscheidet Mobilfunk von Festnetz und lehnt solche Eingaben ab.
 * Der Preis sind rund 70 KB mehr Metadaten (154 statt 83 KB, vor Kompression).
 */
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { CapturedValuesPayload, JobType } from '../../../../redux/jobs/jobs.types';
import { CapturedValuesConfigType } from '../../../../redux/application/application.types';
import { EMAIL_REGEX } from '../../../../constants/RegEx';
import {
    hasExplicitCountryCode,
    OTHER_REGION,
    phoneCallingCode,
    PhoneRegionSelection,
    resolveConfiguredRegion,
    resolveEffectivePhoneRegion,
    toParsableNumber,
} from './JobDetails.phoneRegion';

// Präfix "ksa." ist für erfasste Werte reserviert; ksa-csv-export erkennt sie
// daran und hält sie aus dem generischen Zusatzwert-Export heraus.
export const CAPTURED_VALUE_KEY_PHONE = 'ksa.phone';
export const CAPTURED_VALUE_KEY_EMAIL = 'ksa.email';

// Das Backend begrenzt die Adresse auf 254 Zeichen (@Size(max = 254)).
const EMAIL_MAX_LENGTH = 254;

export interface CapturedValuesInputState {
    phone: string;
    email: string;
    /**
     * Die AUSDRÜCKLICHE Wahl des Ablesers in der Länderauswahl. `undefined` heißt: es gilt
     * die konfigurierte Vorauswahl. Deshalb bleibt `EMPTY_CAPTURED_VALUES_INPUT` ohne Region
     * und das Zurücksetzen beim Auftragswechsel braucht keinen Sonderfall.
     */
    phoneRegion?: PhoneRegionSelection;
}

export const EMPTY_CAPTURED_VALUES_INPUT: CapturedValuesInputState = { phone: '', email: '' };

export interface CapturedValuesVisibility {
    showPhone: boolean;
    showEmail: boolean;
    showBlock: boolean;
}

/**
 * Sucht über alle Tarife und alle Ergebnisse des Auftrags. Die weite Suche ist
 * notwendig: Ab dem zweiten Turnus wird nichts mehr gesendet, der Wert bleibt
 * also dauerhaft an genau dem Ergebnis hängen, in dem er erfasst wurde.
 */
export const hasCapturedValue = (job: JobType, key: string): boolean =>
    (job?.meter?.meterRateTasks ?? []).some((task) =>
        (task?.readingResults ?? []).some((result) =>
            (result?.additionalValues ?? []).some((additionalValue) => additionalValue?.key === key && !!additionalValue?.value?.trim()),
        ),
    );

export const resolveCapturedValuesVisibility = ({
    config,
    job,
}: {
    config?: CapturedValuesConfigType;
    job: JobType;
}): CapturedValuesVisibility => {
    const showPhone = config?.phoneEnabled === true && !hasCapturedValue(job, CAPTURED_VALUE_KEY_PHONE);
    const showEmail = config?.emailEnabled === true && !hasCapturedValue(job, CAPTURED_VALUE_KEY_EMAIL);

    return { showPhone, showEmail, showBlock: showPhone || showEmail };
};

/**
 * Grund einer abgelehnten Telefonnummer. Nur `missingCountryCode` wird unterschieden: bei
 * "Anderes Land" soll der Ableser erfahren, WAS fehlt, nicht nur dass etwas nicht stimmt.
 */
export type PhoneRejection = 'missingCountryCode';

/**
 * Leere Eingabe gilt als gültig ohne Wert — "nicht erfasst" ist kein Fehler.
 * Der Rückgabewert ist E.164 und erfüllt damit das Backend-Muster.
 *
 * `region` ist bewusst als `string` typisiert und nicht als Regionscode: der Wert stammt
 * auch aus der Mandantenkonfiguration und darf dort kleingeschrieben oder unbekannt sein.
 * `resolveConfiguredRegion` fängt das ab (siehe #930 Review, Befund 6).
 */
export const normalizePhone = (input: string, region: string): { value?: string; valid: boolean; reason?: PhoneRejection } => {
    const trimmed = input?.trim() ?? '';
    if (!trimmed) return { valid: true };

    // Ohne vorbelegtes Land gibt es nichts zu ergänzen — die Vorwahl muss dastehen.
    if (region === OTHER_REGION && !hasExplicitCountryCode(trimmed)) {
        return { valid: false, reason: 'missingCountryCode' };
    }

    const parsed =
        region === OTHER_REGION
            ? parsePhoneNumberFromString(toParsableNumber(trimmed))
            : parsePhoneNumberFromString(trimmed, resolveConfiguredRegion(region));

    if (!parsed || !parsed.isValid()) return { valid: false };

    return { value: parsed.number, valid: true };
};

/**
 * Formatiert die Telefonnummer während der Eingabe, also "01701234567" fortlaufend zu
 * "0170 1234567".
 *
 * Ist ein Land gewählt, lässt man die 0 der Ortsvorwahl korrekterweise weg ("+49" steht ja
 * davor). AsYouType gliedert national aber nur MIT der 0 und lässt "341975950" ungegliedert.
 * Solche Eingaben werden deshalb mit der Vorwahl des Landes formatiert und diese danach
 * wieder abgeschnitten: "341975950" → "+49 341 975950" → "341 975950".
 *
 * Beim LÖSCHEN wird nicht formatiert. Ohne diese Ausnahme kommt man nicht über ein
 * eingefügtes Trennzeichen hinweg: die Rücktaste entfernt das Leerzeichen, die Formatierung
 * setzt es sofort wieder ein, und das Feld hängt.
 */
export const formatPhoneAsYouType = (input: string, previous: string, region: string): string => {
    if (input.length < previous.length) return input;

    if (region !== OTHER_REGION && /^\s*[1-9]/.test(input)) {
        const callingCode = phoneCallingCode(resolveConfiguredRegion(region));
        const formatted = new AsYouType().input(`${callingCode}${input}`);

        if (formatted.startsWith(`${callingCode} `)) {
            return formatted.slice(callingCode.length + 1);
        }
    }

    const formatter = region === OTHER_REGION ? new AsYouType() : new AsYouType(resolveConfiguredRegion(region));
    const formatted = formatter.input(input);

    // AsYouType liefert für Eingaben, aus denen es keine Nummer erkennt, einen leeren String.
    // Dann bleibt stehen, was getippt wurde.
    return formatted || input;
};

/**
 * Nur trim(), bewusst keine Kleinschreibung: der lokale Teil einer Adresse ist
 * laut Norm unterscheidungsempfindlich.
 */
export const validateEmail = (input: string): { value?: string; valid: boolean } => {
    const trimmed = input?.trim() ?? '';
    if (!trimmed) return { valid: true };
    if (trimmed.length > EMAIL_MAX_LENGTH) return { valid: false };
    if (!EMAIL_REGEX.test(trimmed)) return { valid: false };

    return { value: trimmed, valid: true };
};

export const buildCapturedValuesPayload = ({
    visibility,
    input,
    defaultPhoneRegion,
}: {
    visibility: CapturedValuesVisibility;
    input: CapturedValuesInputState;
    defaultPhoneRegion: string;
}): CapturedValuesPayload | undefined => {
    const { selection } = resolveEffectivePhoneRegion({
        chosen: input.phoneRegion,
        defaultRegion: defaultPhoneRegion,
        phone: input.phone,
    });

    const phone = visibility.showPhone ? normalizePhone(input.phone, selection).value : undefined;
    const email = visibility.showEmail ? validateEmail(input.email).value : undefined;

    if (!phone && !email) return undefined;

    return {
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
    };
};

export const isCapturedValuesSubmitBlocked = ({
    visibility,
    phoneRequired,
    emailRequired,
    input,
    defaultPhoneRegion,
}: {
    visibility: CapturedValuesVisibility;
    phoneRequired: boolean;
    emailRequired: boolean;
    input: CapturedValuesInputState;
    defaultPhoneRegion: string;
}): boolean => {
    if (visibility.showPhone) {
        const { selection } = resolveEffectivePhoneRegion({
            chosen: input.phoneRegion,
            defaultRegion: defaultPhoneRegion,
            phone: input.phone,
        });
        const phone = normalizePhone(input.phone, selection);
        if (!phone.valid) return true;
        if (phoneRequired && !phone.value) return true;
    }

    if (visibility.showEmail) {
        const email = validateEmail(input.email);
        if (!email.valid) return true;
        if (emailRequired && !email.value) return true;
    }

    return false;
};

/**
 * `capturedValuesInput` lebt als `useState` in der Detailansicht, nicht am
 * Auftrag. Beim Wechsel von einem Auftrag zum nächsten wird die Komponente
 * nicht ausgehängt — nur die Auftragsnummer ändert sich —, daher muss die
 * Eingabe explizit verworfen werden. Sonst bliebe eine bei Auftrag A
 * eingetippte, aber noch nicht abgesendete Telefonnummer/E-Mail-Adresse im
 * Eingabefeld stehen und würde beim Absenden dem Auftrag von Kunde B
 * zugeordnet (siehe #930 Review, Befund 1).
 *
 * Wird sowohl vom `[jobId]`-Effekt (Auftragswechsel) als auch von
 * `handleResend` (erneutes Öffnen der Eingabe nach einem Fehlschlag)
 * aufgerufen — beide Stellen sollen exakt dasselbe tun.
 */
export const resolveCapturedValuesInputAfterReset = (_previousInput: CapturedValuesInputState): CapturedValuesInputState =>
    EMPTY_CAPTURED_VALUES_INPUT;
